import stringFunctions from './stringFunctions';
import dayjs from 'dayjs';

export default {
  getBenefitTitle,
  getTefapIncomeLimit,
  getTefapStatus,
  getTefapReason,
  addressComplete,
  getStateConfig,
  getStatePrograms,
  userParticipates,
  isRequiredTEFAPAddressField,
};

function getTefapStatus(user) {
  const { tefap_attestation_complete, is_eligible_for_tefap, tefap_expiration_date } = user;
  const currentDate = new Date();
  if (!tefap_attestation_complete) {
    return 'incomplete';
  } else if (tefap_expiration_date && dayjs(tefap_expiration_date).isBefore(currentDate)) {
    return 'expired';
  } else if (!is_eligible_for_tefap) {
    return 'ineligible';
  } else {
    return 'eligible';
  }
}

function getBenefitTitle(name) {
  if (name.indexOf('benefits') !== -1) {
    return name.replace('_benefits', '').toUpperCase();
  } else {
    const { capitalize } = stringFunctions;
    return name
      .split('_')
      .map((part) => capitalize(part))
      .join(' ');
  }
}

function getTefapIncomeLimit(user, tefapConfig) {
  const { adult_family_members, child_family_members, senior_family_members } = user;
  const getInt = (value) => parseInt(value || 0, 10);
  const members =
    1 + getInt(adult_family_members) + getInt(child_family_members) + getInt(senior_family_members);

  // get state-specific income limits table from global TEFAP config
  const stateConfig = getStateConfig(tefapConfig, user);
  const table = stateConfig && stateConfig.rows;
  if (!table) {
    // TODO: Return a default value?
    return;
  }

  // cap family size at 8 members, for looking up income limit
  const size = Math.min(members, 8);

  // return income limit for size
  const row = table.find((row) => row.size === size);
  let income = row && row.income;

  const additional = members - size;
  if (additional > 0 && stateConfig.additional) {
    // row income values are already formatted as USD (from the backend), but the "additional" value is a plain integer
    // so we need to parse the row income value before doing any math with it, and then reformat it as USD.
    income = getInt(income.replaceAll(/(\$|,)/g, ''));
    income += additional * stateConfig.additional;
    const usd = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });
    income = usd.format(income);
  }

  return income;
}

/**
 * Get the reason why a person is elibible or ineligble for tefap
 * @param {object} user        The user object
 * @param {object} translation The translation object
 * @param {object} tefapConfig The global TEFAP configuation object, keyed by state code
 * @param {object} pantry      Optional, the pantry where user gets service
 */
function getTefapReason(user, translation, tefapConfig, pantry) {
  const { adult_family_members, child_family_members, senior_family_members } = user;
  const getInt = (value) => parseInt(value || 0, 10);
  const members =
    1 + getInt(adult_family_members) + getInt(child_family_members) + getInt(senior_family_members);

  const max_limit = getTefapIncomeLimit(user, tefapConfig);
  const family = `${members} ${members > 1 ? translation.people : translation.person}`;
  const reason = { reason: '', programs: false, income: false };

  // ineligible because out-of-state?
  if (pantry && pantry.state && pantry.state !== '') {
    if (user && user.state && user.state !== '') {
      if (pantry.state !== user.state) {
        reason.out_of_state = true;
        reason.reason = translation.tefap_out_of_state;
        return reason;
      }
    }
  }

  // eligible because of one of the programs?
  const tefapPrograms = getStatePrograms(tefapConfig, user);
  const eligiblePrograms = tefapPrograms.filter((program) => userParticipates(user, program.id));
  if (eligiblePrograms.length == 1) {
    reason.programs = true;
    reason.reason += `${translation.tefap_eligible_program}: ${getBenefitTitle(eligiblePrograms[0].name)}`;
  } else if (eligiblePrograms.length > 1) {
    reason.programs = true;
    reason.reason += `${translation.tefap_eligible_programs}:`;
    for (let i = 0; i < eligiblePrograms.length; i++) {
      if (i < eligiblePrograms.length - 2) {
        reason.reason += ` ${getBenefitTitle(eligiblePrograms[i].name)},`;
      } else if (i == eligiblePrograms.length - 2) {
        reason.reason += ` ${getBenefitTitle(eligiblePrograms[i].name)}`;
      } else {
        reason.reason += ` ${translation.and} ${getBenefitTitle(eligiblePrograms[i].name)}`;
      }
    }
  }

  // eligible because of income?
  if (user.below_min_household_income) {
    reason.income = true;
    let iReason = `${translation.tefap_household_income} ${family}`;
    iReason += ` ${translation.not_exceeds} ${max_limit}`;
    if (!reason.programs) {
      reason.reason = iReason;
    } else {
      reason.reason += ` ${translation.and} ${iReason}`;
    }
  }

  // not eligble for any reason
  if (!reason.programs && !reason.income) {
    reason.reason = `${translation.tefap_not_eligible_program} ${translation.and} `;
    reason.reason += `${translation.tefap_household_income} ${family} ${translation.exceeds} ${max_limit}`;
  }

  return reason;
}

/**
 * Determines if a specified field key is required for a pantry's address.
 * This function is specifically used in the context of TEFAP attestation.
 * If the pantry is located in New York (NY), only the 'state' field is required.
 * For pantries in other states, the required fields are 'address_street_1', 'city', 'state', and 'zip_code'.
 *
 * @param {Object} pantry - The pantry object containing information about the pantry.
 * @param {string} pantry.state - The state in which the pantry is located.
 * @param {string} key - The key of the address field to check for requirement.
 * @returns {boolean} True if the specified key is a required field for the pantry's address, false otherwise.
 */
function isRequiredTEFAPAddressField(pantry, key) {
  if (pantry.state === 'NY' || pantry.state === 'CO') {
    return ['state', 'zip_code'].includes(key);
  }

  return ['address_street_1', 'city', 'state', 'zip_code'].includes(key);
}

/**
 * Returns true for valid 5-digit zip codes
 * @param zipCode
 * @returns {boolean}
 */
function isValidFiveDigitZipCode(zipCode) {
  return /^\d{5}$/.test(zipCode);
}

/**
 * Checks if the provided address is complete. An address is considered complete if it is in New York (NY) state or
 * if it includes a 5-digit zip code, a city name longer than one character, a street address longer than one character,
 * and a 2-character state code.
 *
 * @param {Object} pantry - The pantry object
 * @param {Object} values - The object containing address values.
 * @param {string} [values.state] - The state code of the address.
 * @param {string} [values.zip_code] - The zip code of the address.
 * @param {string} [values.city] - The city name of the address.
 * @param {string} [values.address_street_1] - The street address.
 * @returns {boolean} Returns true if the address is complete based on the specified criteria, false otherwise.
 */
function addressComplete(pantry, values) {
  if (pantry?.state === 'NY' || pantry?.state === 'CO') {
    const stateIsComplete = values?.state?.length == 2;
    const zipCodeIsComplete = isValidFiveDigitZipCode(values?.zip_code);
    return stateIsComplete && zipCodeIsComplete;
  }

  // default
  if (
    isValidFiveDigitZipCode(values?.zip_code) &&
    values?.city?.length > 1 &&
    values?.address_street_1?.length > 1 &&
    values?.state?.length == 2
  ) {
    return true;
  }
  return false;
}

/**
 * Get the state-specific TEFAP configuration that applies for a user, based on their residence
 * @param {object} globalConfig The global TEFAP configuation object, keyed by state code
 * @param {object} user         The user object
 */
function getStateConfig(globalConfig, user) {
  // default to NY as state?
  const tefapState = (user && user.state) || 'NY';
  const stateConfig = globalConfig[tefapState] || {};

  return stateConfig;
}

/**
 * Get the state-specific TEFAP public assitance programs that apply for a user, based on their residence
 * @param {object} globalConfig The global TEFAP configuation object, keyed by state code
 * @param {object} user         The user object
 */
function getStatePrograms(globalConfig, user) {
  const stateConfig = getStateConfig(globalConfig, user);
  const statePrograms = (stateConfig && stateConfig.programs) || [];

  return statePrograms;
}

/**
 * Determine whether a user participates in a public assistance program
 * @param {object} user       The user object
 * @param {number} program_id The id of the public assistance program
 */
function userParticipates(user, program_id) {
  const programs = user.assistance_programs || [];
  const program = programs.find((program) => program.public_assistance_program_id === program_id);

  return program && program.participates;
}
